import React, { useEffect, useState } from 'react';
import { ComboProduct, SelectedOption, VariantsEntity } from '../../@types';
import { OptionKeys } from '../../../cart/@types';
import Loading from '../Loading';
import './index.css';
import Popup from '../Popup';
import { fetchCart, updateIconCount } from '../../utils';

const api =
  process.env.NODE_ENV !== 'development'
    ? 'https://asia-southeast1-enda-combine-stock-shopify-app.cloudfunctions.net'
    : process.env.NGROK_DEV;

const ComboModule = ({
  productId,
  shop,
  productTitle,
  cartIconClass,
  productColourSwatches,
  shopCurrency,
}: {
  productId: string;
  shop: string;
  productTitle: string;
  cartIconClass: string | undefined;
  productColourSwatches: string[];
  shopCurrency: string;
}) => {
  const [busy, setBusy] = useState(false);
  const [comboId, setComboId] = useState(`COMBO${(Math.random() * 10000).toFixed(0)}`);
  const [qty, setQty] = useState(1);
  const [products, setProducts] = useState<ComboProduct[][]>([]);
  const [selectedOptions, setSelectedOptions] = useState<{ [index: number]: SelectedOption }>({});
  const [selectedProductChoices, setSelectedProductChoices] = useState<any[]>([]);
  const [selectedComboProducts, setSelectedComboProducts] = useState<
    { variantId: number; productId: number | undefined }[]
  >([]);
  const [popupData, setPopupData] = useState(null);

  const isVariantOutOfStock = (variant: VariantsEntity) => {
    return variant.inventory_quantity === 0 && variant.inventory_policy === 'deny';
  };

  const swatchMap = productColourSwatches.reduce((acc, curr) => {
    const [key, value] = curr.split(':').map((str) => str.trim());
    acc[key] = value;
    return acc;
  }, {} as { [key: string]: string });

  const handleProductChoice = (productIndex: number, index: number, product: ComboProduct) => {
    const updatedSelectedProductChoices = [...selectedProductChoices];
    updatedSelectedProductChoices[productIndex] = index;

    // console.log(product, 'product')

    setSelectedProductChoices(updatedSelectedProductChoices);

    const updatedSelectedComboProducts = [...selectedComboProducts];
    if (product?.variants?.length === 1) {
      updatedSelectedComboProducts[productIndex] = {
        variantId: product?.variants?.[0].id,
        productId: product.id,
      };
    }

    setSelectedComboProducts(updatedSelectedComboProducts);

    setSelectedOptions((prev) => ({
      ...prev,
      [productIndex]: {},
    }));
  };

  const shopCurrencyValue = shopCurrency;

  useEffect(() => {
    // console.log({ selectedOptions, selectedComboProducts, selectedProductChoices })
    // set selected combo products based on selected options
    // const selectedComboProducts: { variantId: number; productId: number }[] = new Array( // set array of selectedCombo Product based on product length
    //   products.length,
    // ).fill(null);

    products.forEach((product, productIndex) => {
      product.forEach((p, i) => {
        // select first variant if there's just one option
        if (p.variants?.length === 1 && !isVariantOutOfStock(p.variants[0])) {
          // selectedComboProducts[productIndex] = {
          //   variantId: p?.variants?.[0].id,
          //   productId: p.id
          // };
          selectedComboProducts[productIndex] = selectedComboProducts[productIndex];
          return;
        }
        const selectedOption = selectedOptions[productIndex];
        let selectedVariants = [] as any[];
        if (selectedProductChoices[productIndex] === i) {
          selectedVariants =
            (selectedOption &&
              p.variants?.filter((variant) => {
                return Object.keys(selectedOption).every((key) => {
                  return selectedOption[key as OptionKeys] === variant[key as OptionKeys];
                });
              })) ||
            [];
        }
        if (selectedVariants.length === 1 && !isVariantOutOfStock(selectedVariants[0])) {
          selectedComboProducts[productIndex] = {
            variantId: selectedVariants[0].id,
            productId: p.id,
          };
        }
      });
    });

    setSelectedComboProducts(selectedComboProducts);
  }, [selectedOptions, setSelectedComboProducts, selectedProductChoices]); // setSelectedComboProducts is set based on selectedOptions and selectedProductChoices

  useEffect(() => {
    // fetch product
    const fetchComboProducts = async () => {
      const response = await fetch(`${api}/getComboProducts?id=${productId}&shop=${shop}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();

      if (data && data.products) {
        setProducts(data.products);

        // Initialize with `null` instead of `0`
        const initialSelectedProductChoices = new Array(data.products.length).fill(null);
        setSelectedProductChoices(initialSelectedProductChoices);
      } else {
        setProducts([]);
      }
    };
    fetchComboProducts();
  }, [productId, shop]);

  const handleSelectOption = (
    productIndex: number,
    index: number,
    optionName: OptionKeys,
    optionValue: string,
  ) => {
    // console.log({ productIndex, index, optionName, optionValue });
    // check if already checked
    const selectedOption = selectedOptions[productIndex];
    if (selectedOption && selectedOption[optionName] === optionValue) {
      setSelectedOptions((prev) => {
        const selectedOption = { ...prev[productIndex] };
        delete selectedOption[optionName];
        return {
          ...prev,
          [productIndex]: selectedOption,
        };
      });
      return;
    } else {
      setSelectedOptions((prev) => ({
        ...prev,
        [productIndex]: {
          ...prev[productIndex],
          [optionName]: optionValue,
        },
      }));
    }
  };

  const handleAddToCart = async () => {
    setBusy(true);
    try {
      const response = await fetch(`/cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: selectedComboProducts
            .filter((e) => e)
            .map((product) => ({
              id: product.variantId,
              quantity: qty,
              properties: {
                _combo_id: productId,
                Combo: comboId,
                'Combo Name': productTitle,
              },
            })),
          // sections: 'cart-items,cart-icon-bubble,cart-live-region-text,cart-footer',
        }),
      });
      setComboId(`COMBO${(Math.random() * 10000).toFixed(0)}`);
      const data = await response.json();
      // console.log('data', data);
      // todo popup added items to cart
      setPopupData(data);
      const cart = await fetchCart();
      updateIconCount(cart, cartIconClass);
    } catch (err) {
      console.log('*err*', err);
    } finally {
      setBusy(false);
    }
  };

  const handleButtonClick = () => {
    const allSelectedProductChoices = selectedProductChoices.every(
      (item) => item !== null && item !== undefined,
    );
    const allSelectedComboProducts = selectedComboProducts.every(
      (item) => item !== null && item !== undefined,
    );
    if (
      selectedComboProducts.length === products.length &&
      allSelectedProductChoices &&
      allSelectedComboProducts
    ) {
      handleAddToCart();
    } else {
      alert('Please select all products before adding to cart.');
      console.log('Please select all products before adding to cart.');
    }
  };

  const handleClosePopup = () => {
    setPopupData(null);
  };

  if (products.length === 0) {
    return (
      <div className="rp-container">
        <Loading />
      </div>
    );
  }

  // console.log({selectedOptions, selectedComboProducts, selectedProductChoices})
  // console.log('products', products);
  return (
    <div className="rp-container">
      {products.map((productRow, productIndex) => (
        <div key={productIndex}>
          <hr />
          <span style={{ fontSize: '12px' }}>Please select one(1) product:</span>
          {productRow.map((product, index) => {
            // console.log("product", product)
            // console.log('index', index);
            // console.log('productIndex', productIndex);
            const getPriceOrRange = (selectedOptions: SelectedOption | null) => {
              const formatPrice = (price: number, currency: string) => {
                if (currency === 'Rp') {
                  // currency Rp, remove 2 decimal places.
                  return price.toLocaleString(undefined, {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  });
                } else {
                  // Other currencies, format with 2 decimal places.
                  return price.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  });
                }
              };

              if (product.variants?.length === 1) {
                const variant = product.variants[0];
                const price = parseFloat(variant.price);
                if (!isNaN(price)) {
                  return shopCurrencyValue + ' ' + formatPrice(price, shopCurrencyValue);
                } else {
                  return ''; // Handle invalid price
                }
              }

              const prices = (product.variants || [])
                .filter((variant) => {
                  return Object.keys(selectedOptions || {}).every((key) => {
                    return (
                      (selectedOptions || {})[key as OptionKeys] === variant[key as OptionKeys]
                    );
                  });
                })
                .map((variant) => parseFloat(variant.price))
                .filter((price) => !isNaN(price)); // Filter out invalid prices

              if (prices.length === 0) {
                return ''; // Handle case where there are no valid prices
              }

              if (prices.every((price) => price === prices[0])) {
                return shopCurrencyValue + ' ' + formatPrice(prices[0], shopCurrencyValue);
              } else {
                const minPrice = Math.min(...prices);
                const maxPrice = Math.max(...prices);
                return `${shopCurrencyValue}${formatPrice(
                  minPrice,
                  shopCurrencyValue,
                )} - ${shopCurrencyValue}${formatPrice(maxPrice, shopCurrencyValue)}`;
              }
            };
            const price = getPriceOrRange(selectedOptions[product.id]);
            const isOutOfStock = product.variants?.every(isVariantOutOfStock);
            const isSelected = selectedProductChoices[productIndex] === index;

            const selectedComboProduct = selectedComboProducts[productIndex];
            let imageSrc = product.image.src; // default image when no variant selected

            // console.log({product, selectedComboProduct})
            let imageProduct;
            if (selectedOptions[productIndex]) {
              const productColor = selectedOptions[productIndex].option1;

              const variantProduct = product.variants?.find(
                (variant) => variant.option1 === productColor,
              );

              if (variantProduct) {
                imageProduct = product.images?.find(
                  (image) => image.id === variantProduct.image_id,
                );
              }
            }

            if (selectedComboProduct && selectedComboProduct.variantId) {
              const selectedVariant = product?.variants?.find(
                (variant) => variant.id === selectedComboProduct.variantId,
              );
              if (selectedVariant) {
                const variantImage = product?.images?.find(
                  (image) => image.id === selectedVariant.image_id,
                );
                if (variantImage) {
                  imageSrc = variantImage.src;
                }
              }
            }

            return (
              <label
                htmlFor={`productChoice${productIndex}-${index}`}
                className="rp-title-container"
                style={{
                  border: `2px solid ${isSelected ? '#6f1920' : '#f7f1f0'}`,
                  cursor: 'pointer',
                  borderRadius: '5px',
                  padding: '15px',
                  margin: '10px',
                }}
              >
                <div className="rp-card-combo" key={product.id + index}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      placeItems: 'center',
                    }}
                  >
                    <img
                      src={imageProduct?.src ? imageProduct?.src : product.image.src}
                      style={{ width: '10%', marginRight: '10px', borderRadius: '3px' }}
                    />
                    <span className="rp-product-title">{product.title}</span>
                    <span className={`rp-price${isOutOfStock ? ' oos' : ''}`}>
                      {isOutOfStock ? 'Out of Stock' : `+ ${price}`}
                    </span>
                    {/* radio button for product choices */}
                    <input
                      id={`productChoice${productIndex}-${index}`}
                      type="radio"
                      name={`productChoice${productIndex}`}
                      style={{ display: 'none' }}
                      checked={selectedProductChoices[productIndex] === index ? true : false}
                      onChange={() => handleProductChoice(productIndex, index, product)}
                    />
                  </div>
                  <div className="rp-card-combo" key={product.id + index}>
                    {product.variants?.length === 1 ? null : ( // to check the variant of the product. if 1 then it does not have variants
                      <div>
                        {selectedProductChoices[productIndex] === index ? (
                          <div className="rp-card-option-container-combo">
                            {product.options?.map((option, productOptionIndex) => {
                              if (product.variants?.length === 1) return null;
                              // console.log('selectedOptions', selectedOptions);
                              // console.log('productOptionIndex', productOptionIndex);
                              const optionValue =
                                selectedOptions[productIndex]?.[
                                  `option${option.position}` as OptionKeys
                                ];
                              // selectedProductChoices.map((selectedProd) => {
                              //   if (selectedProd === product.id) {
                              //     console.log('kenekenekene');
                              //   }
                              // });
                              return (
                                <div className="rp-card-option" key={option.id}>
                                  <div>
                                    {/* display the product option eg, size/color */}
                                    <span className="rp-option-name">{option.name}</span>
                                    <span className="rp-option-value">
                                      {optionValue && selectedProductChoices[productIndex] === index
                                        ? `: ${optionValue}`
                                        : null}
                                    </span>
                                  </div>
                                  <div className="rp-card-option-values-container">
                                    {option.values?.map((value, optionIndex) => {
                                      const possibleVariants = (product.variants || []).filter(
                                        (variant) => {
                                          // check if variant is available if selected
                                          const futureSelectedOptions = {
                                            ...selectedOptions[productIndex],
                                            [`option${option.position}` as OptionKeys]: value,
                                          };
                                          return Object.keys(futureSelectedOptions).every((key) => {
                                            return (
                                              futureSelectedOptions[key as OptionKeys] ===
                                              variant[key as OptionKeys]
                                            );
                                          });
                                        },
                                      );

                                      // function to check inventory for sale with considering selected combo products
                                      const inventoryForSale = (variant: VariantsEntity) => {
                                        const selectedVariants = selectedComboProducts.filter(
                                          (e) => e?.variantId === variant.id,
                                        );
                                        if (variant.inventory_management === null) return 999;
                                        if (variant.inventory_policy !== 'deny') return 999;
                                        return variant.inventory_quantity - selectedVariants.length;
                                      };

                                      // check if all possible variants are out of stock
                                      const isDisabled = possibleVariants.every((variant) => {
                                        return (
                                          inventoryForSale(variant) === 0 ||
                                          inventoryForSale(variant) < 0
                                        );
                                      });

                                      return (
                                        <div
                                          className={`rp-combo-option-item rp-option-item-combo${
                                            option.name === 'colour' ||
                                            option.name === 'color' ||
                                            option.name === 'Colour' ||
                                            option.name === 'Color'
                                              ? ' rp-option-colour-swatches'
                                              : ''
                                          }`}
                                          key={value}
                                          data-swatch={value}
                                          style={
                                            swatchMap[value]
                                              ? {
                                                  backgroundColor: swatchMap[value],
                                                  padding: '0rem',
                                                }
                                              : undefined
                                          }
                                        >
                                          <label>
                                            {/* Radio button for color && size */}
                                            <input
                                              disabled={isDisabled}
                                              type="radio"
                                              id={productIndex + product.id + value}
                                              name={productIndex + option.name}
                                              value={value}
                                              checked={optionValue === value}
                                              onClick={(e) =>
                                                handleSelectOption(
                                                  productIndex,
                                                  index,
                                                  `option${option.position}` as OptionKeys,
                                                  value,
                                                )
                                              }
                                            />
                                            {swatchMap[value] ? '' : value}
                                          </label>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : null}
                      </div>
                    )}
                  </div>
                </div>
              </label>
            );
          })}
        </div>
      ))}
      {popupData && <Popup data={popupData} popupTitle={productTitle} onClose={handleClosePopup} />}

      <div className="rp-add-button intan-add-button">
        <button className="button altcolour btn--secondary" onClick={handleButtonClick}>
          Add to Cart
        </button>
      </div>
    </div>
  );
};

export default ComboModule;
